/** กติกาแพ็กเกจและรอบการนับโควตา (pure ไม่ใช้เวลาปัจจุบันเอง ส่ง now เข้ามาเสมอ) */
export const PLAN_IDS = ["free", "basic", "standard", "premium"] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const PLAN_RANK: Record<PlanId, number> = { free: 0, basic: 1, standard: 2, premium: 3 };

export const PERIOD_DAYS = 30;
const DAY_MS = 86_400_000;
const BKK_MS = 7 * 3_600_000;

export interface SubscriptionState {
  plan: PlanId;
  /** เวลาเริ่มแพ็กเกจต่อเนื่อง (ISO instant) รอบโควตานับจากนี้ทีละ 30 วัน */
  planStart: string;
  /** เวลาหมดอายุ (ISO instant) ; free = null */
  periodEnd: string | null;
}

/** วันที่ตามเวลา Asia/Bangkok รูปแบบ YYYY-MM-DD */
export function bangkokDate(d: Date): string {
  return new Date(d.getTime() + BKK_MS).toISOString().slice(0, 10);
}

/** แพ็กเกจที่มีผลอยู่จริง: แพ็กเกจเสียเงินที่หมดอายุแล้วกลับเป็น free */
export function effectivePlan(sub: SubscriptionState, now: Date): PlanId {
  if (sub.plan === "free") return "free";
  return sub.periodEnd && now.getTime() < Date.parse(sub.periodEnd) ? sub.plan : "free";
}

export interface QuotaWindow {
  /** ใช้เป็น key ของ UsageCounter */
  key: string;
  start: Date;
  end: Date;
}

/**
 * รอบนับโควตาปัจจุบัน
 * - free: เดือนปฏิทินตามเวลา Asia/Bangkok
 * - แพ็กเกจเสียเงินที่ยังไม่หมดอายุ: ทีละ 30 วันนับจาก planStart
 */
export function quotaWindow(sub: SubscriptionState, now: Date): QuotaWindow {
  if (effectivePlan(sub, now) === "free") {
    const b = new Date(now.getTime() + BKK_MS);
    const y = b.getUTCFullYear();
    const m = b.getUTCMonth();
    const start = new Date(Date.UTC(y, m, 1) - BKK_MS);
    const end = new Date(Date.UTC(y, m + 1, 1) - BKK_MS);
    return { key: bangkokDate(start), start, end };
  }
  const t0 = Date.parse(sub.planStart);
  const k = Math.max(0, Math.floor((now.getTime() - t0) / (PERIOD_DAYS * DAY_MS)));
  const start = new Date(t0 + k * PERIOD_DAYS * DAY_MS);
  // key เป็นเวลาเริ่มรอบแบบเต็ม เพื่อให้อัปเกรดในวันเดียวกันเริ่มรอบใหม่ (รีเซ็ตโควตา) ได้
  return { key: start.toISOString(), start, end: new Date(start.getTime() + PERIOD_DAYS * DAY_MS) };
}

export type PurchaseKind = "new" | "renew" | "upgrade";
export type PurchaseResult = { ok: true; kind: PurchaseKind; next: SubscriptionState } | { ok: false; error: "downgrade_blocked" | "free_not_purchasable" };

/**
 * ผลของการชำระเงิน
 * - ซื้อแพ็กเกจเดิมที่ยังไม่หมดอายุ: ต่อจากวันหมดอายุเดิม (+30 วัน) รอบโควตายังเดินต่อ
 * - อัปเกรด: มีผลทันที เริ่มรอบใหม่ (รีเซ็ตโควตา) 30 วันจากเวลาชำระ (เวลาที่เหลือของแพ็กเกจเดิมไม่ทบ)
 * - ซื้อแพ็กเกจต่ำกว่าที่ใช้อยู่ไม่ได้ ; หมดอายุแล้วนับเป็นซื้อใหม่
 */
export function applyPurchase(sub: SubscriptionState, purchased: PlanId, paidAt: Date): PurchaseResult {
  if (purchased === "free") return { ok: false, error: "free_not_purchasable" };
  const active = effectivePlan(sub, paidAt);
  const periodMs = PERIOD_DAYS * DAY_MS;
  if (active === purchased) {
    const end = Date.parse(sub.periodEnd!) + periodMs;
    return { ok: true, kind: "renew", next: { plan: purchased, planStart: sub.planStart, periodEnd: new Date(end).toISOString() } };
  }
  if (PLAN_RANK[purchased] < PLAN_RANK[active]) return { ok: false, error: "downgrade_blocked" };
  const kind: PurchaseKind = active === "free" ? "new" : "upgrade";
  return { ok: true, kind, next: { plan: purchased, planStart: paidAt.toISOString(), periodEnd: new Date(paidAt.getTime() + periodMs).toISOString() } };
}

/** เหลือกี่วัน (ปัดขึ้น) ก่อนหมดอายุ ; null ถ้า free */
export function daysLeft(sub: SubscriptionState, now: Date): number | null {
  if (effectivePlan(sub, now) === "free" || !sub.periodEnd) return null;
  return Math.max(0, Math.ceil((Date.parse(sub.periodEnd) - now.getTime()) / DAY_MS));
}
